import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Clock } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Timetable = () => {
  const { user } = useAuth();
  const [timetable, setTimetable] = useState({ entries: [], grouped: {} });
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    class_id: '', subject_id: '', teacher_id: '', day_of_week: '', start_time: '', end_time: '', room: '',
  });

  const weekDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  useEffect(() => { fetchData(); }, [user?.school_id]);
  useEffect(() => { if (selectedClass) fetchTimetable(); }, [selectedClass, user?.school_id]);

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    const handler = () => {
      fetchData();
      if (selectedClass) fetchTimetable();
    };
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [selectedClass, user?.school_id]);

  const fetchData = async () => {
    try {
      const academicYearId = getSelectedAcademicYearId();
      const classParams = academicYearId ? { academic_year_id: academicYearId } : {};
      const [classesRes, subjectsRes, teachersRes] = await Promise.all([
        api.get('/classes', { params: classParams }),
        api.get('/subjects'),
        api.get('/teachers'),
      ]);
      setClasses(classesRes.data.data || []);
      setSubjects(subjectsRes.data.data || []);
      setTeachers(teachersRes.data.data || []);
    } catch (error) {
      console.error('Failed to fetch data');
    }
  };

  const fetchTimetable = async () => {
    try {
      const academicYearId = getSelectedAcademicYearId();
      const params = {
        class_id: selectedClass,
        ...(academicYearId ? { academic_year_id: academicYearId } : {}),
      };
      const response = await api.get('/timetable', { params });
      setTimetable(response.data.data || { entries: [], grouped: {} });
    } catch (error) {
      console.error('Failed to fetch timetable');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const academicYearId = getSelectedAcademicYearId();
      await api.post('/timetable', { ...formData, academic_year_id: academicYearId || undefined });
      toast({ title: 'Success', description: 'Timetable entry created' });
      setIsDialogOpen(false);
      setFormData({ class_id: '', subject_id: '', teacher_id: '', day_of_week: '', start_time: '', end_time: '', room: '' });
      if (selectedClass) fetchTimetable();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to create entry', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6" data-testid="timetable-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Timetable</h1>
          <p className="text-slate-400">Manage class schedules</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-2" /> Add Period</Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700 text-white">
            <DialogHeader><DialogTitle>Add Timetable Entry</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Class *</Label>
                  <Select value={formData.class_id} onValueChange={(v) => setFormData({...formData, class_id: v})}>
                    <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">{classes.map(c => <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Subject *</Label>
                  <Select value={formData.subject_id} onValueChange={(v) => setFormData({...formData, subject_id: v})}>
                    <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">{subjects.map(s => <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Teacher *</Label>
                  <Select value={formData.teacher_id} onValueChange={(v) => setFormData({...formData, teacher_id: v})}>
                    <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">{teachers.map(t => <SelectItem key={t.id} value={t.id.toString()}>{t.first_name} {t.last_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Day *</Label>
                  <Select value={formData.day_of_week} onValueChange={(v) => setFormData({...formData, day_of_week: v})}>
                    <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">{weekDays.map(d => <SelectItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Start Time *</Label><Input type="time" value={formData.start_time} onChange={(e) => setFormData({...formData, start_time: e.target.value})} className="bg-slate-700 border-slate-600" required /></div>
                <div><Label>End Time *</Label><Input type="time" value={formData.end_time} onChange={(e) => setFormData({...formData, end_time: e.target.value})} className="bg-slate-700 border-slate-600" required /></div>
                <div><Label>Room</Label><Input value={formData.room} onChange={(e) => setFormData({...formData, room: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">Add Entry</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Class Filter */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <Select value={selectedClass} onValueChange={setSelectedClass}>
            <SelectTrigger className="w-48 bg-slate-700 border-slate-600 text-white"><SelectValue placeholder="Select Class" /></SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">{classes.map(c => <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Timetable Grid */}
      {!selectedClass ? (
        <Card className="bg-slate-800 border-slate-700"><CardContent className="py-12 text-center"><p className="text-slate-400">Select a class to view timetable</p></CardContent></Card>
      ) : (
        <div className="grid gap-4">
          {weekDays.map(day => (
            <Card key={day} className="bg-slate-800 border-slate-700">
              <CardHeader className="py-3">
                <CardTitle className="text-white capitalize">{day}</CardTitle>
              </CardHeader>
              <CardContent>
                {(timetable.grouped[day] || []).length === 0 ? (
                  <p className="text-slate-500 text-sm">No classes scheduled</p>
                ) : (
                  <div className="flex flex-wrap gap-3">
                    {(timetable.grouped[day] || []).map(entry => (
                      <div key={entry.id} className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg">
                        <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                          <Clock className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                          <p className="font-medium text-white">{entry.subject_name}</p>
                          <p className="text-xs text-slate-400">{entry.start_time?.slice(0,5)} - {entry.end_time?.slice(0,5)} • {entry.teacher_first_name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Timetable;
